<template>
  <div class="container p-0 m-0">
    <!-- Voortgang. Een <ol> met aria-current="step" vertelt méér dan een
         progressbar en heeft geen live-regio nodig.

         Buiten beeld gezet: deze onderdelen zijn op 1 september 2026 in
         productie zichtbaar geworden op ruim honderd bestaande formulieren, die
         nooit met een zichtbare kop of voortgangsbalk zijn ontworpen. Ze staan
         wél gewoon in de toegankelijkheidsboom, dus voor een schermlezer
         verandert er niets: die hoort nog steeds op welke stap hij is en hoeveel
         er zijn. Een besluit om ze te tonen is een ontwerpkeuze per formulier,
         geen toegankelijkheidseis. -->
    <nav v-if="paginaTitels.length > 1" aria-label="Voortgang van het formulier" class="visually-hidden">
      <ol class="stappen">
        <li v-for="(titel, i) in paginaTitels" :key="i"
            :aria-current="i === Number(internalPageIndex) ? 'step' : undefined"
            :class="{ 'stap-actief': i === Number(internalPageIndex) }">
          <span class="visually-hidden">Stap {{ i + 1 }} van {{ paginaTitels.length }}: </span>{{ titel }}
        </li>
      </ol>
    </nav>

    <!-- Het formulier had geen enkele kop. Dat is een echt probleem: een
         pagina zonder kop heeft voor een schermlezer geen naam, en bij een
         paginawissel was er geen plek om de focus heen te sturen — die viel
         terug op <body>, waarna je opnieuw door alles heen moest tabben.

         De kop blijft daarom bestaan, maar buiten beeld. Hij staat niet in de
         tabvolgorde (tabindex=-1, alleen programmatisch focusbaar), dus SC 2.4.7
         over zichtbare focus is hier niet in het geding.

         pageDescription wordt bewust niet meer gerenderd. Op 159 van de 191
         productiepagina's staat daar de letterlijke standaardwaarde "Pagina
         omschrijving" die PageManager bij het aanmaken wegschrijft. Die tekst
         voorlezen is slechter dan hem weglaten, en zichtbaar tonen was nooit de
         bedoeling. -->
    <h1 ref="paginaKop" tabindex="-1" class="visually-hidden">{{ kopTekst }}</h1>

    <!-- Eén foutensamenvatting in plaats van veertien gelijktijdige
         veldmeldingen; elke regel springt naar het bijbehorende veld. -->
    <div v-if="foutenLijst.length" ref="foutenSamenvatting" tabindex="-1" role="alert"
         class="alert alert-danger mt-3">
      <h2 class="h6">Er {{ foutenLijst.length === 1 ? 'is 1 veld' : 'zijn ' + foutenLijst.length + ' velden' }} niet goed ingevuld</h2>
      <ul class="mb-0">
        <li v-for="fout in foutenLijst" :key="fout.id">
          <a :href="'#' + fout.id" @click.prevent="springNaarVeld(fout.id)">{{ fout.label }}</a>
        </li>
      </ul>
    </div>

    <form ref="myForm" :class="{ 'was-validated': wasValidated }" class="row g-3 needs-validation ms-1 mt-2"
      @submit.prevent="handleNextOrSubmit" novalidate>
      <FormElement v-for="(element, index) in elements" :element="element" :pageIndex="Number(internalPageIndex)" :key="index"
        :targetId="'element-' + index" :elementIndex="index" :lang="lang" :formData="formData" :formVersion="formVersion"
        :wasValidated="wasValidated" @elementChanged="elementChanged" required :hidden="isHidden(element)"
        :defaultLabelPosition="formStructure && formStructure.defaultLabelPosition" />

      <SReCaptcha @onRecaptchaTokenResponse="onRecaptchaTokenResponse" class="mb-3"
        v-if="isLastPage && !formStructure.hideCaptcha" />

      <!-- De knoppen stonden buiten het <form>, dus voor hulpsoftware hoorden
           ze nergens bij. -->
      <div class="d-flex justify-content-end mb-2">
        <button type="button" class="btn btn-secondary ms-1" :hidden="internalPageIndex == 0" @click.stop="prevPage">&lt; Vorige</button>
        <button type="submit" :class="isLastPageOfForm(internalPageIndex) ? 'btn-primary' : 'btn-secondary'" class="btn ms-1">
          {{ isLastPage ? 'Verzenden' : 'Volgende >' }}
        </button>
      </div>
    </form>
    <div v-if="alertVisible" class="alert alert-warning alert-dismissible fade show" role="alert">{{ alertMessage }}
      <button type="button" class="btn-close" @click="alertVisible = false" aria-label="Melding sluiten" />
    </div>
    <div class="d-flex justify-content-end mb-2">
      <small class="text-muted" style="font-size: 10px;">v{{ formVersion }}</small>
    </div>
  </div>
</template>

<script lang="ts">
import FormElement from './SFormElement.vue'
import SReCaptcha from '../SReCaptcha.vue'
import { defineComponent } from 'vue'
import ServiceFactory from '../../services/ServiceFactory'
import { IFormSubmitService } from '../../services/IFormSubmitService'

/**
 * PageManager in de beheeromgeving schrijft bij een nieuwe pagina letterlijke
 * standaardwaarden weg — het zijn opgeslagen waarden, geen placeholders. In
 * productie staat daardoor op 113 pagina's "New Page" als Engelse paginanaam.
 * Die als kop aan een schermlezer aanbieden zegt niets; dan is de formuliernaam
 * of het stapnummer bruikbaarder.
 */
const STANDAARD_PAGINANAMEN = ['Nieuwe Pagina', 'New Page']

export default defineComponent({
  name: 'FormBuilder',
  props: ['lang', 'formStructure', 'formId', 'pageIndex'],
  components: { FormElement, SReCaptcha },
  data() {
    return {
      pageTitle: '',
      internalPageIndex: this.pageIndex ?? 0,
      pageDescription: '',
      pages: [],
      page: null as any,
      elements: [],
      formData: {} as any,
      wasValidated: false,
      isLastPage: false,
      showRecaptcha: true,
      loadingTimeout: 30000,
      alertVisible: false,
      alertMessage: '' as string,
      formService: {} as IFormSubmitService,
      formVersion: 0,
      foutenLijst: [] as Array<{ id: string; label: string }>,
      // Alleen focus verplaatsen als de gebruiker zélf bladert. Bij de eerste
      // render of bij de browser-terugknop is focus stelen zelf een probleem.
      bladerenDoorGebruiker: false,
    }
  },
  computed: {
    paginaTitels(): string[] {
      const pages: any[] = (this.formStructure && this.formStructure.pages) || []
      return pages.map((p: any, i: number) => {
        const titel = ((p.pageTitle && p.pageTitle[this.lang]) || '').trim()
        return titel && !STANDAARD_PAGINANAMEN.includes(titel) ? titel : `Stap ${i + 1}`
      })
    },

    /**
     * De tekst van de verborgen kop: de paginanaam, en anders de naam van het
     * formulier. Nooit leeg, want de kop is ook het focusanker bij een
     * paginawissel en moet dus altijd bestaan.
     */
    kopTekst(): string {
      const titel = (this.pageTitle || '').trim()
      if (titel && !STANDAARD_PAGINANAMEN.includes(titel)) return titel
      const formulier =
        (this.formStructure && this.formStructure.formTitle && this.formStructure.formTitle[this.lang]) || ''
      return formulier || `Stap ${Number(this.internalPageIndex) + 1}`
    },
  },
  watch: {
    formStructure(value: any) {
      if (value) {
        this.initData()
      }
    },
    pageIndex(newVal) {
      if (typeof newVal === 'number') {
        this.internalPageIndex = newVal
        this.initData()
        this.isLastPage = this.isLastPageOfForm(newVal)
        this.foutenLijst = []
        if (this.bladerenDoorGebruiker) {
          this.bladerenDoorGebruiker = false
          this.$nextTick(() => {
            const kop = this.$refs.paginaKop as HTMLElement | undefined
            if (kop) kop.focus()
            window.scrollTo(0, 0)
          })
        }
      }
    }
  },
  methods: {
    /**
     * Verzamel de ongeldige velden en hun labeltekst voor de samenvatting.
     * Verborgen velden worden overgeslagen: die zijn met :disabled uitgesloten
     * van validatie, maar een extra check kost niets en voorkomt verwarring.
     */
    verzamelFouten() {
      const form = this.$refs.myForm as HTMLFormElement | undefined
      if (!form) return []
      const gezien = new Set<string>()
      const fouten: Array<{ id: string; label: string }> = []
      for (const el of Array.from(form.elements) as any[]) {
        if (!el.willValidate || el.disabled || el.type === 'hidden') continue
        if (el.checkValidity()) continue
        // Radio- en checkboxgroepen delen een naam; één regel per groep.
        const sleutel = el.name || el.id
        if (!sleutel || gezien.has(sleutel)) continue
        gezien.add(sleutel)
        // Sommige velden valideren op een control die buiten beeld staat (het
        // geo-veld doet dat, omdat een readonly veld niet gevalideerd wordt).
        // data-focus-voor wijst dan naar het element dat de gebruiker wél kan
        // bedienen.
        const focusDoel = el.dataset && el.dataset.focusVoor ? el.dataset.focusVoor : el.id
        fouten.push({ id: focusDoel, label: this.labelVoor(el) })
      }
      return fouten
    },

    /**
     * Documenttitel per stap. Alle drie de pagina's heetten "ProjectenMonitor",
     * en dat is precies de tekst die een schermlezer voorleest bij binnenkomst
     * en bij het wisselen van venster. Zonder de stapnaam weet je dus niet waar
     * je bent (WCAG 2.4.2).
     */
    zetDocumentTitel() {
      const app = (window as any).__APP_CONFIG__?.appName || 'ProjectenMonitor'
      const formulier =
        (this.formStructure && this.formStructure.formTitle && this.formStructure.formTitle[this.lang]) || ''
      // kopTekst en niet pageTitle: anders staat "New Page" in de venstertitel.
      const delen = [this.kopTekst, formulier, app].filter(Boolean)
      // Dubbele delen weglaten: bij een formulier van één pagina heten de stap
      // en het formulier vaak hetzelfde.
      document.title = [...new Set(delen)].join(' — ')
    },

    /**
     * Labeltekst opschonen voor de foutensamenvatting. Het label bevat een
     * asterisk en een verborgen "(verplicht)"; in een opsomming van fouten
     * leest dat als ruis, en de reden staat er sowieso al bij.
     */
    schoonLabel(tekst: string): string {
      return tekst
        .replace(/\(verplicht\)/gi, '')
        .replace(/[*\u2217]/g, '')
        .replace(/\s+/g, ' ')
        .trim()
    },

    labelVoor(el: HTMLElement): string {
      const doc = document
      // Een veld mag zijn eigen naam meegeven; nodig waar het gevalideerde
      // element niet het element is waar het label bij hoort.
      const eigenNaam = (el as HTMLElement).dataset?.veldnaam
      if (eigenNaam) return this.schoonLabel(eigenNaam)

      const naamViaGroep = () => {
        const groep = el.closest('[aria-labelledby]')
        const labelId = groep && groep.getAttribute('aria-labelledby')
        const viaGroep = labelId ? doc.getElementById(labelId) : null
        return viaGroep && viaGroep.textContent ? this.schoonLabel(viaGroep.textContent) : ''
      }

      // Bij een radio- of checkboxgroep hoort de naam van de groep in de
      // samenvatting, niet die van de eerste optie: een regel "Isolatie"
      // vertelt niet welk veld ontbreekt, "Aanvullende maatregelen" wel.
      const type = (el as HTMLInputElement).type
      if (type === 'radio' || type === 'checkbox') {
        const groepsnaam = naamViaGroep()
        if (groepsnaam) return groepsnaam
      }

      const gekoppeld = el.id ? doc.querySelector(`label[for="${CSS.escape(el.id)}"]`) : null
      if (gekoppeld && gekoppeld.textContent) return this.schoonLabel(gekoppeld.textContent)
      const groepsnaam = naamViaGroep()
      if (groepsnaam) return groepsnaam
      return (el as HTMLInputElement).name || 'Onbenoemd veld'
    },

    springNaarVeld(id: string) {
      const el = document.getElementById(id)
      if (el) {
        el.scrollIntoView({ block: 'center' })
        ;(el as HTMLElement).focus()
      }
    },

    elementChanged(name: string, data: any) {
      console.log('SFormBuilder.elementChanged', name, data)
      this.formData[name] = data
    },
    initData() {
      console.log("initData: ", this.formStructure.pages)
      if (this.formStructure && this.formStructure.pages && this.lang) {
        this.formVersion = Number(this.formStructure.formVersion)
        this.pages = this.formStructure.pages
        this.page = this.pages[this.internalPageIndex]
        this.elements = this.page.elements
        this.pageTitle = this.page.pageTitle[this.lang]
        this.zetDocumentTitel()
        this.pageDescription = this.page.pageDescription[this.lang]
        this.isLastPage = this.isLastPageOfForm(this.internalPageIndex)
        
        // Initialize form data from form structure
        this.initFormDataFromFormStructure()
        this.applyUrlParamValues()
      }
    },
    initFormDataFromFormStructure() {
      console.log('[SFormBuilder] Initializing form data from form structure...');
      
      if (!this.formStructure || !this.formStructure.pages) {
        console.warn('[SFormBuilder] No form structure available for initialization');
        return;
      }
      
      // Loop through all pages and elements to initialize form data
      this.formStructure.pages.forEach((page: any) => {
        if (page.elements) {
          page.elements.forEach((element: any) => {
            if (element.name && !(element.name in this.formData)) {
              // Initialize with appropriate default value based on element type
              let defaultValue = null;
              
              switch (element.type) {
                case 'text':
                case 'email':
                case 'textarea':
                  defaultValue = '';
                  break;
                case 'number':
                case 'amount':
                  defaultValue = null;
                  break;
                case 'select':
                case 'radio':
                  defaultValue = '';
                  break;
                case 'checkbox':
                  defaultValue = [];
                  break;
                case 'geo':
                  defaultValue = null;
                  break;
                default:
                  defaultValue = '';
                  break;
              }
              
              this.formData[element.name] = defaultValue;
              console.log(`[SFormBuilder] Initialized field '${element.name}' (${element.type}) with:`, defaultValue);
            }
          });
        }
      });
      
      console.log('[SFormBuilder] Form data after initialization:', this.formData);
    },
    applyUrlParamValues() {
      if (!this.formStructure || !this.formStructure.pages) return

      // Gebruik URLSearchParams als fallback voor $route.query (robuuster bij hash-routing)
      const urlParams = new URLSearchParams(window.location.search)
      const routeQuery = this.$route?.query ?? {}
      console.log('[SFormBuilder] applyUrlParamValues — $route.query:', routeQuery, '| window.location.search:', window.location.search)

      this.formStructure.pages.forEach((page: any) => {
        if (!page.elements) return
        page.elements.forEach((element: any) => {
          if (
            element.valueSource &&
            element.valueSource.type === 'urlParam' &&
            element.valueSource.key
          ) {
            const key = element.valueSource.key
            const paramValue = (routeQuery[key] as string) || urlParams.get(key) || ''
            console.log(`[SFormBuilder] Checking URL-param '${key}' for field '${element.name}': '${paramValue}'`)
            if (paramValue) {
              this.formData[element.name] = paramValue
              console.log(`[SFormBuilder] URL-param pre-fill: '${element.name}' = '${paramValue}'`)
            }
          } else if (element.name) {
            console.log(`[SFormBuilder] Field '${element.name}' has no valueSource:`, element.valueSource)
          }
        })
      })
    },
    isHidden(element: any) {
      if (element.hidden === 'public') return false
      return element.hidden
    },
    prevPage() {
      this.bladerenDoorGebruiker = true
      if (this.internalPageIndex > 0) {
        const prev = this.internalPageIndex - 1
        this.$router.push({ query: { ...this.$route.query, page: prev } })
        this.wasValidated = false;
      }
    },
    nextPage() {
      this.bladerenDoorGebruiker = true
      if (this.internalPageIndex < this.formStructure.pages.length - 1) {
        const next = this.internalPageIndex + 1
        this.$router.push({ query: { ...this.$route.query, page: next } })
      }
    },
    isLastPageOfForm(pageIndex: number) {
      if (!this.pages || this.pages.length == 0) {
        return false;
      }
      if (this.pages.length == 1) {
        return true;
      }
      return pageIndex == this.pages.length - 1
    },
    handleNextOrSubmit() {
      console.log('[SFormBuilder] handleNextOrSubmit called');
      this.wasValidated++;
      console.log('[SFormBuilder] wasValidated incremented to:', this.wasValidated);
      
      if (this.checkFormValidity()) {
        this.foutenLijst = []
        if (this.isLastPageOfForm(this.internalPageIndex)) {
          this.submitForm(this.formData)
        } else {
          this.wasValidated = false;
          this.nextPage()
        }
      } else {
        // Samenvatting tonen en de focus erheen. Zonder dit blijft de focus op
        // de verzendknop staan en merkt een schermlezergebruiker niet dat er
        // iets mis is.
        this.foutenLijst = this.verzamelFouten()
        this.$nextTick(() => {
          const blok = this.$refs.foutenSamenvatting as HTMLElement | undefined
          if (blok) blok.focus()
          else if (this.foutenLijst.length) this.springNaarVeld(this.foutenLijst[0].id)
        })
      }
    },
    checkFormValidity() {
      const form = this.$refs.myForm;
      console.log('[SFormBuilder] Checking form validity, form element:', form);
      
      // FIRST: Clean up orphaned inputs BEFORE checking validity
      this.cleanupOrphanedInputs(form);
      
      const isValid = form.checkValidity();
      console.log('[SFormBuilder] Form validity result:', isValid);
      
      // Als form invalid is, log details (maar zonder cleanup want die is al gedaan)
      if (!isValid) {
        console.warn('[SFormBuilder] Form is invalid, running detailed check...');
        this.logFormValidationDetailsOnly();
      }
      
      return isValid;
    },
         logFormValidationDetails() {
       console.log('[SFormBuilder] === DETAILED FORM VALIDATION CHECK ===');
       const form = this.$refs.myForm;
       if (!form) {
         console.error('[SFormBuilder] Form ref not found!');
         return;
       }
       
       console.log('[SFormBuilder] Form element:', form);
               console.log('[SFormBuilder] Form validity:', form.checkValidity());
        console.log('[SFormBuilder] Form validation message:', form.validationMessage);
       
       const inputs = form.querySelectorAll('input, select, textarea');
       console.log('[SFormBuilder] Found form elements:', inputs.length);
       
               let invalidElements: any[] = [];
       
       inputs.forEach((input: any, index: number) => {
         const isValid = input.checkValidity();
         const elementInfo = {
           index: index,
           tagName: input.tagName,
           type: input.type,
           name: input.name,
           id: input.id,
           required: input.required,
           value: input.value,
           className: input.className,
           validity: input.validity ? Object.keys(input.validity).reduce((acc: any, key: string) => {
             acc[key] = input.validity[key];
             return acc;
           }, {}) : 'no validity object',
           validationMessage: input.validationMessage,
           isValid: isValid
         };
         
         if (!isValid) {
           invalidElements.push(elementInfo);
           console.error(`[SFormBuilder] ❌ INVALID Element ${index}:`, elementInfo);
         } else {
           console.log(`[SFormBuilder] ✅ Valid Element ${index}:`, {
             name: input.name,
             type: input.type,
             value: input.value
           });
         }
       });
       
       console.log('[SFormBuilder] === SUMMARY ===');
       console.log(`[SFormBuilder] Total elements: ${inputs.length}`);
       console.log(`[SFormBuilder] Invalid elements: ${invalidElements.length}`);
       
       if (invalidElements.length > 0) {
         console.error('[SFormBuilder] ❌ REASONS FOR FORM INVALIDITY:');
         invalidElements.forEach((element, index) => {
           console.error(`[SFormBuilder] ${index + 1}. ${element.name || element.id || 'unnamed'} (${element.type}): ${element.validationMessage}`);
         });
       }
       
               // Expose debug function to global scope
        (window as any).debugFormValidation = () => this.logFormValidationDetails();
        console.log('[SFormBuilder] 💡 You can call debugFormValidation() from console to run this again');
      },
      logFormValidationDetailsOnly() {
        console.log('[SFormBuilder] === DETAILED FORM VALIDATION CHECK (NO CLEANUP) ===');
        const form = this.$refs.myForm;
        if (!form) {
          console.error('[SFormBuilder] Form ref not found!');
          return;
        }
        
        console.log('[SFormBuilder] Form element:', form);
        console.log('[SFormBuilder] Form validity:', form.checkValidity());
        console.log('[SFormBuilder] Form validation message:', form.validationMessage);
        
        const inputs = form.querySelectorAll('input, select, textarea');
        console.log('[SFormBuilder] Found form elements:', inputs.length);
        
        let invalidElements: any[] = [];
        
        inputs.forEach((input: any, index: number) => {
          const isValid = input.checkValidity();
          const elementInfo = {
            index: index,
            tagName: input.tagName,
            type: input.type,
            name: input.name,
            id: input.id,
            required: input.required,
            value: input.value,
            className: input.className,
            validity: input.validity ? Object.keys(input.validity).reduce((acc: any, key: string) => {
              acc[key] = input.validity[key];
              return acc;
            }, {}) : 'no validity object',
            validationMessage: input.validationMessage,
            isValid: isValid
          };
          
          if (!isValid) {
            invalidElements.push(elementInfo);
            console.error(`[SFormBuilder] ❌ INVALID Element ${index}:`, elementInfo);
          } else {
            console.log(`[SFormBuilder] ✅ Valid Element ${index}:`, {
              name: input.name,
              type: input.type,
              value: input.value
            });
          }
        });
        
        console.log('[SFormBuilder] === SUMMARY ===');
        console.log(`[SFormBuilder] Total elements: ${inputs.length}`);
        console.log(`[SFormBuilder] Invalid elements: ${invalidElements.length}`);
        
        if (invalidElements.length > 0) {
          console.error('[SFormBuilder] ❌ REASONS FOR FORM INVALIDITY:');
          invalidElements.forEach((element, index) => {
            console.error(`[SFormBuilder] ${index + 1}. ${element.name || element.id || 'unnamed'} (${element.type}): ${element.validationMessage}`);
          });
        }
      },
      cleanupOrphanedInputs(form: any) {
        console.log('[SFormBuilder] Cleaning up orphaned inputs...');
        const inputs = form.querySelectorAll('input[type="number"]');
        let removedCount = 0;
        
        inputs.forEach((input: any) => {
          // Zoek naar number inputs zonder naam die lat/lng waarden bevatten
          const hasNoName = !input.name || input.name.trim() === '';
          const hasNoId = !input.id || input.id.trim() === '';
          const value = parseFloat(input.value);
          const isLatLngValue = !isNaN(value) && Math.abs(value) > 1 && Math.abs(value) < 180;
          
          if (hasNoName && hasNoId && isLatLngValue) {
            console.warn('[SFormBuilder] Removing orphaned lat/lng input:', {
              value: input.value,
              element: input
            });
            input.remove();
            removedCount++;
          }
        });
        
        console.log(`[SFormBuilder] Removed ${removedCount} orphaned inputs`);
      },
    async onRecaptchaTokenResponse(value: string) {
      const result = await this.formService.submitReCaptcha(value)
      console.log('onRecaptchaTokenResponse', result)
    },
    async submitForm(formData: { _lang: string, __template: string }) {
      console.log('submitForm', formData)
      this.alertVisible = false
      this.alertMessage = ''
      formData._lang = this.lang
      const response = await this.formService.submitForm(this.formId, formData)
      if (response.success) {
        this.$router.push(`/submit/confirm?id=${this.formId}`)
      } else {
        this.alertVisible = true;
        this.alertMessage = response.message
      }

    }
  },
  async created() {
    await this.initData()
    this.formService = ServiceFactory.getService('formSubmit')
    setTimeout(() => {
      this.isLastPage = this.isLastPageOfForm(this.internalPageIndex)
    }, 100)

  },
  mounted() {
    // Expose debug functions to global scope for console debugging
    (window as any).debugForm = this;
    (window as any).debugFormValidation = () => this.logFormValidationDetails();
    (window as any).debugFormData = () => {
      console.log('[DEBUG] Current form data:', this.formData);
      console.log('[DEBUG] Form structure:', this.formStructure);
      console.log('[DEBUG] Was validated:', this.wasValidated);
    };
    console.log('[SFormBuilder] 🔧 Debug functions available: debugForm, debugFormValidation(), debugFormData()');
  }
})
</script>

<style scoped>
/* Voortgangsstappen. Bewust een <ol>: de volgorde is betekenisvol, en
   aria-current markeert waar de gebruiker is zonder live-regio. */
.stappen {
  display: flex;
  flex-wrap: wrap;
  gap: 0.35rem 1rem;
  list-style: none;
  padding: 0;
  margin: 0 0 0.5rem;
  font-size: 0.875rem;
  color: #5c6b7a;
}
.stappen li::before {
  content: counter(list-item) '. ';
}
.stap-actief {
  font-weight: 600;
  color: var(--primary, #0f6c65);
}

/* Landingsplek voor de focus na een paginawissel. Die focus moet zichtbaar
   zijn: zonder ring ziet een toetsenbordgebruiker niet dat hij is verplaatst,
   drukt nog een keer Tab en denkt dat de sprong niet werkte. :focus-visible
   zorgt dat een muisklik geen ring oplevert. */
h1[tabindex="-1"]:focus-visible,
[role="alert"][tabindex="-1"]:focus-visible {
  outline: 3px solid var(--primary, #0f6c65);
  outline-offset: 4px;
  border-radius: 2px;
}
</style>
